/**
 * PRD.330 Phase 2 — Report summary bar: matching-record count + the
 * report's aggregates, exactly as supplied by the query result (full
 * filtered result — never a page subtotal). Wording and formatting come
 * from the consumer; nothing report-specific is hard-coded here.
 */
import type { ReactNode } from 'react';
import type { ReportSummaryItem } from './report-view-logic';

export function ReportSummary({
  totalCount,
  countLabel,
  items,
  formatAggregate,
  known,
  refetching,
  note,
}: {
  totalCount: number;
  /** Plural noun for the count, e.g. "payments". */
  countLabel: string;
  items: ReadonlyArray<ReportSummaryItem>;
  formatAggregate: (key: string, value: number) => ReactNode;
  /** False while loading or on error — values render as "—", never stale. */
  known: boolean;
  refetching: boolean;
  /** Optional informational note rendered under the summary. */
  note?: ReactNode;
}) {
  return (
    <div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 'var(--sp-3) var(--sp-6)',
          padding: 'var(--sp-3) var(--sp-4)',
          background: 'var(--bg-surface-2, #f8fafc)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
        }}
      >
        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text)' }}>
          <strong style={{ fontWeight: 'var(--font-weight-semi)' }}>
            {known ? totalCount : '—'}
          </strong>{' '}
          {countLabel}
          {refetching && (
            <span
              style={{
                marginLeft: 'var(--sp-2)',
                color: 'var(--text-muted)',
                fontSize: 'var(--font-size-xs)',
              }}
            >
              Updating…
            </span>
          )}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-6)' }}>
          {items.map((item) => (
            <div
              key={item.key}
              style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}
            >
              {item.label}{' '}
              <strong
                style={{
                  color: 'var(--text)',
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 'var(--font-weight-semi)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {known && item.value !== null ? formatAggregate(item.key, item.value) : '—'}
              </strong>
            </div>
          ))}
        </div>
      </div>
      {note && (
        <div
          style={{
            marginTop: 'var(--sp-1)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-muted)',
          }}
        >
          {note}
        </div>
      )}
    </div>
  );
}
